import { get, isEmpty, pick, size } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { getUnmodeledDates } from "selectors/dates"
import { getLatestDate, getEarliestDate } from "modules/portfolio-root/selectors/dates"
import { getFilterOns, getFilter, getNormalization, getSegment, getClassificationsTiers, getChipSecuritiesIds, getAddedFilters } from "./selectors/filters"
import { getSelectedExperiment } from "modules/edit-experiment/selectors"
import { getSelectedModelId, getCurrency, getPortfolioId, getIsExpanded, getPortfolioFxHedgeEnabled } from "modules/portfolio-root/selectors/portfolios"
import { getBasePositionSet, getBaseSignature } from "selectors/base"
import { Creators as RootCreators } from "modules/portfolio-root/model"
import { Creators as EditExperimentCreators } from "modules/edit-experiment/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getClassifications, generateClassificationQuery } from "helpers/classification"
import { generateIntradayBatchUrl } from "helpers/intraday"
import { Creators } from "./model"
import { getMissingDataFiles, betasAreMissing } from "selectors/missingDataFiles"
import { getSortedContentSets } from "selectors/contentSets"
import { getIsInvalidBetaInstrumentReason, getPortfolioBetaInstrument } from "modules/portfolio-root/selectors/beta-instrument"
import { getRebalanceDate, getRebalanceMetrics, getSelectedDataType, getOrder, getWidgetField, getDecompositionType, getAccountId, getIsRebalanceRunning, getIdSort, getSelectedPage, getIsActiveTradingDisabled, getMetricTypeOptions, getSelectedMetricType } from "./selectors"
import { getColumnsByCategory } from "./selectors/columns"
import { getFactorsCategoriesHeader, getTrades, getExperiment, getSortedAndPaginatedDataWithEditable, getNumberOfTrades, getAggregatedData, getNumberOfContributors, getGroupDropdown, getRawCombinedContributors } from "./selectors/data"
import { getAttributionMethod, getRiskContributors } from "./selectors/risk/index"
import { getSelectedContentSet } from "./selectors/exposure"
import { getForecastSecurities, getForecasts, getForecast } from "./selectors/forecast"
import { getPortfolioPositions, getWorkingEquity, getIncludeNotHeld } from "./selectors/positions"
import { getIsOpPro } from "modules/account-root/selectors"
import { getAiTermsAccepted } from "modules/account-root/selectors"
import { getHasData, getLastRefresh, getIntradayLastBatchId } from "modules/intraday/selectors"
import { compositionQuery, groupedCompositionQuery, positionHoldingsQuery } from "./helpers/queries/composition"
import { betaQuery, betaContributorsQuery, groupedBetaContributorsQuery, dynamicBetaQuery, dynamicBetaContributorsQuery, groupedDynamicBetaContributorsQuery } from "./helpers/queries/beta"
import { forecastQuery, forecastContributorsQuery, groupedForecastContributorsQuery } from "./helpers/queries/forecast"
import { contentSetExposureContributorsQuery, groupedContentSetExposureContributorsQuery, contentSetExposureQuery, exposureContributorsQuery, groupedExposureContributorsQuery, exposureQuery } from "./helpers/queries/exposure"
import { riskContributorsQuery, groupedRiskContributorsQuery, riskQuery } from "./helpers/queries/risk"
import { riskContributorsFactorsQuery, groupedRiskContributorsFactorsQuery, riskFactorsQuery } from "./helpers/queries/riskFactors"
import { factorSensitivityContributorsQuery, groupedFactorSensitivityContributorsQuery, factorSensitivityQuery } from "./helpers/queries/factorSensitivity"
import { marginalContributorsQuery, groupedMarginalContributorsQuery } from "./helpers/queries/marginal"
import { getIsIntraday, getIsActiveAndNotIntraday, getIsIntradayRoute, getIsIntradayRebalance, getIntradayBatchId, getIntradayRefreshable } from "./selectors/intraday"
import { getTradeDownload, getDownloadData } from "./selectors/download"
import { getGrouping, getAvailableRollups, getGroupingOptions } from "./selectors/grouping"
import { getVariables, getQueryId } from "./helpers/queries/helpers"
import { getSummaryRows } from "./selectors/summary"
import { DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES, requiresRiskFactors } from "./helpers"
import { MODEL_METADATA_QUERY } from "resources/models"
import { FORECAST_LIST_QUERY } from "resources/forecasts"

const COMMON_PROPS = ["modelId", "selectedDate", "portfolioAliasOrId", "isActive", "baseSignature", "currency", "isIntraday", "intradayBatchId", "analyticsSegment", "expand", "fxHedgeEnabled"]

export default createModule({
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: getAccountId(state, props),
    portfolioAliasOrId: state.global.selected.portfolio,
    portfolioUUID: getPortfolioId(state, props),
    modelId: getSelectedModelId(state, props),
    selectedDate: getRebalanceDate(state, props),
    base: getBasePositionSet(state, props),
    baseSignature: getBaseSignature(state, props),
    forecastAndHorizon: getForecast(state, props),
    missingDataFiles: getMissingDataFiles(state, props),
    classifications: getClassifications(state, props),
    classificationsTiers: getClassificationsTiers(state, props),
    contentSets: getSortedContentSets(state, props),
    betaInstrument: getPortfolioBetaInstrument(state, props),
    currency: getCurrency(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    expand: getIsExpanded(state, props),
    isActive: getIsActiveAndNotIntraday(state, props),
    isOpPro: getIsOpPro(state, props),
    selectedContentSet: getSelectedContentSet(state, props),
    // checks if intraday is enabled and that we are under the Intraday route
    isIntraday: getIsIntraday(state, props),
    intradayBatchId: getIntradayBatchId(state, props),
    attributionMethod: getAttributionMethod(state, props),
    isInvalidBetaInstrumentReason: getIsInvalidBetaInstrumentReason(state, props),
    groupingOptions: getGroupingOptions(state, props),
    forecastSecurities: getForecastSecurities(state, props),
    analyticsSegment: getSegment(state, props),
    securitiesIds: getChipSecuritiesIds(state, props),
    decompositionType: getDecompositionType(state, props),
    fxHedgeEnabled: getPortfolioFxHedgeEnabled(state),
  }),
  required: ["accountId", "modelId", "decompositionType"],
  data: [{
    resource: "classificationsValues",
    as: null,
    url: ({ classificationsTiers, selectedDate }) => {
      if (!classificationsTiers || size(classificationsTiers) === 0 || !selectedDate) {
        return null
      }

      // We don't know the depth of each classification, generate each classification sub query according to the tier depth
      const innerQuery = generateClassificationQuery(classificationsTiers)
      return {
        id: `classificationsValues-${selectedDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
            ${innerQuery}
          }`,
      }
    },
  }, {
    resource: "simulatorPositionHoldings",
    url: (props) => {
      const id = `simulatorPositionHoldings-${getQueryId(props, [...COMMON_PROPS])}`
      const query = positionHoldingsQuery()
      const variables = getVariables(props, query)
      return { id, query, variables }
    },
  }, {
    resource: "simulatorComposition",
    url: (props) => {
      const { isActive, classifications, isIntraday, intradayBatchId, analyticsSegment, expand } = props
      if (isIntraday && !intradayBatchId) { return null }

      const query = compositionQuery(classifications, { isActive, analyticsSegment, expand })
      return {
        id: `simulatorComposition-${getQueryId(props, [...COMMON_PROPS, "classifications"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "simulatorGroupedComposition",
    url: (props) => {
      const { isActive, classifications, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand } = props
      if ((isIntraday && !intradayBatchId) || !groupingOptions) { return null }

      const query = groupedCompositionQuery(classifications, { isActive, analyticsSegment, expand })
      return {
        id: `simulatorGroupedComposition-${getQueryId(props, [...COMMON_PROPS, "classifications", "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  },
  {
    resource: "beta",
    url: (props) => {
      const { decompositionType, selectedDate, missingDataFiles, isIntraday, isActive, intradayBatchId, analyticsSegment, expand } = props
      if (decompositionType !== "beta" || !selectedDate || !missingDataFiles || betasAreMissing(missingDataFiles)) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = betaQuery(missingDataFiles, { isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `beta-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  },
  {
    resource: "betaContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, missingDataFiles, isIntraday, intradayBatchId, analyticsSegment, expand } = props
      if (decompositionType !== "beta" || !selectedDate || !missingDataFiles || betasAreMissing(missingDataFiles)) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = betaContributorsQuery(missingDataFiles, { isIntraday, isActive, analyticsSegment, expand })
      return {
        id: `betaContributors-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedBetaContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, missingDataFiles, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand } = props
      if (decompositionType !== "beta" || !selectedDate || !missingDataFiles || betasAreMissing(missingDataFiles) || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedBetaContributorsQuery(missingDataFiles, { isIntraday, isActive, analyticsSegment, expand })
      return {
        id: `groupedBetaContributors-${getQueryId(props, [...COMMON_PROPS, "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  },
  {
    resource: "dynamicBeta",
    url: (props) => {
      const { decompositionType, selectedDate, betaInstrument, isIntraday, isActive, intradayBatchId, isInvalidBetaInstrumentReason, analyticsSegment, expand } = props
      if (decompositionType !== "beta" || !betaInstrument || isInvalidBetaInstrumentReason || !selectedDate || isIntraday) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = dynamicBetaQuery({ isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `dynamicBeta-${getQueryId(props, [...COMMON_PROPS, "betaInstrument"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  },
  {
    resource: "dynamicBetaContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, betaInstrument, isIntraday, intradayBatchId, isInvalidBetaInstrumentReason, analyticsSegment, expand } = props
      if (decompositionType !== "beta" || !betaInstrument || isInvalidBetaInstrumentReason || !selectedDate || isIntraday) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = dynamicBetaContributorsQuery(betaInstrument, { isIntraday, isActive, analyticsSegment, expand })
      return {
        id: `dynamicBetaContributors-${getQueryId(props, [...COMMON_PROPS, "betaInstrument"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedDynamicBetaContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, betaInstrument, isIntraday, intradayBatchId, isInvalidBetaInstrumentReason, groupingOptions, analyticsSegment, expand } = props
      if (decompositionType !== "beta" || !betaInstrument || isInvalidBetaInstrumentReason || !selectedDate || isIntraday || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedDynamicBetaContributorsQuery(betaInstrument, { isIntraday, isActive, analyticsSegment, expand })
      return {
        id: `groupedDynamicBetaContributors-${getQueryId(props, [...COMMON_PROPS, "betaInstrument", "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "exposure",
    url: (props) => {
      const { decompositionType, selectedDate, isIntraday, isActive, intradayBatchId, analyticsSegment, expand } = props
      if (decompositionType !== "exposure" || !selectedDate) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = exposureQuery({ isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `exposure-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "exposureContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isIntraday, isActive, intradayBatchId, analyticsSegment, expand } = props
      if (decompositionType !== "exposure" || !selectedDate) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = exposureContributorsQuery({ isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `exposureContributors-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedExposureContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isIntraday, isActive, intradayBatchId, groupingOptions, analyticsSegment, expand } = props
      if (decompositionType !== "exposure" || !selectedDate || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedExposureContributorsQuery({ isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `groupedExposureContributors-${getQueryId(props, [...COMMON_PROPS, "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "contentSetExposure",
    url: (props) => {
      const { selectedDate, contentSets, selectedContentSet, isIntraday, intradayBatchId, isActive, analyticsSegment, expand } = props
      if (isEmpty(contentSets) || !selectedDate || !selectedContentSet) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = contentSetExposureQuery(selectedContentSet, { isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `contentSetExposure-${getQueryId(props, [...COMMON_PROPS, "selectedContentSet"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  },
  {
    resource: "contentSetContributors",
    url: (props) => {
      const { selectedDate, contentSets, isActive, selectedContentSet, isIntraday, intradayBatchId, analyticsSegment, expand } = props
      if (isEmpty(contentSets) || !selectedDate || !selectedContentSet) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = contentSetExposureContributorsQuery(selectedContentSet, { isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `contentSetContributors-${getQueryId(props, [...COMMON_PROPS, "selectedContentSet"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedContentSetContributors",
    url: (props) => {
      const { selectedDate, contentSets, isActive, selectedContentSet, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand } = props
      if (isEmpty(contentSets) || !selectedDate || !selectedContentSet || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedContentSetExposureContributorsQuery(selectedContentSet, { isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `groupedContentSetContributors-${getQueryId(props, [...COMMON_PROPS, "selectedContentSet", "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "risk",
    url: (props) => {
      const { selectedDate, isOpPro, isIntraday, intradayBatchId, isActive, analyticsSegment, expand } = props
      if (!selectedDate || isOpPro) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = riskQuery({ isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `risk-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "riskContributors",
    url: (props) => {
      const { selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, analyticsSegment, expand, attributionMethod } = props
      if (!selectedDate || isOpPro) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = riskContributorsQuery({ isActive, isIntraday, analyticsSegment, expand, attributionMethod })
      return {
        id: `riskContributors-${getQueryId(props, [...COMMON_PROPS, "attributionMethod"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedRiskContributors",
    url: (props) => {
      const { selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand, attributionMethod } = props
      if (!selectedDate || isOpPro || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedRiskContributorsQuery({ isActive, isIntraday, analyticsSegment, expand, attributionMethod })
      return {
        id: `groupedRiskContributors-${getQueryId(props, [...COMMON_PROPS, "attributionMethod", "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "riskFactors",
    url: (props) => {
      const { decompositionType, selectedDate, isOpPro, isIntraday, intradayBatchId, isActive, analyticsSegment, expand } = props
      if (!requiresRiskFactors(decompositionType) || !selectedDate || isOpPro) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = riskFactorsQuery({ isActive, isIntraday, analyticsSegment, expand })
      return {
        id: `risk-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "riskContributorsFactors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, analyticsSegment, expand, attributionMethod } = props
      if (!requiresRiskFactors(decompositionType) || !selectedDate || isOpPro) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = riskContributorsFactorsQuery({ isActive, isIntraday, analyticsSegment, expand, attributionMethod })
      return {
        id: `riskContributorsFactors-${getQueryId(props, [...COMMON_PROPS, "attributionMethod"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedRiskContributorsFactors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand, attributionMethod } = props
      if (decompositionType !== "risk" || !selectedDate || isOpPro || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedRiskContributorsFactorsQuery({ isActive, isIntraday, analyticsSegment, expand, attributionMethod })
      return {
        id: `groupedRiskContributorsFactors-${getQueryId(props, [...COMMON_PROPS, "attributionMethod", "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "marginalContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, analyticsSegment, expand, attributionMethod } = props
      if (decompositionType !== "marginal" || !selectedDate || isOpPro) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = marginalContributorsQuery({ isActive, isIntraday, analyticsSegment, expand, attributionMethod })
      return {
        id: `marginalContributors-${getQueryId(props, [...COMMON_PROPS, "attributionMethod"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedMarginalContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand, attributionMethod } = props
      if (decompositionType !== "marginal" || !selectedDate || isOpPro || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedMarginalContributorsQuery({ isActive, isIntraday, analyticsSegment, expand, attributionMethod })
      return {
        id: `groupedMarginalContributors-${getQueryId(props, [...COMMON_PROPS, "attributionMethod", "groupingOptions"])}`,
        options: {
          ignoreMessages: true, // prevent repeated marginal contribution warnings
        },
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    ...FORECAST_LIST_QUERY,
    as: null,
  }, {
    resource: "impliedReturnsForecast",
    url: (props) => {
      const { forecastAndHorizon, isOpPro, isIntraday, isActive, analyticsSegment, expand } = props
      const { forecast, horizon } = forecastAndHorizon
      // If a forecast is selected, we're using this query to get the summary number
      if (isIntraday || (isOpPro && !forecast)) { return null }
      const query = forecastQuery({ forecast, horizon }, { isActive, isIntraday, analyticsSegment, expand })
      return {
        // Implied returns are calculated once per portfolio and won't change unless the horizon is being changed
        // So we only need to have the horizon in the key
        id: `forecast-${getQueryId({ ...props, forecastAndHorizon }, [...COMMON_PROPS, "forecastAndHorizon"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "forecastContributors",
    url: (props) => {
      const { forecastAndHorizon, forecastSecurities, isOpPro, isIntraday, isActive, analyticsSegment, expand } = props
      const isImpliedReturn = !get(forecastAndHorizon, "forecast.id")

      if (isImpliedReturn && isEmpty(forecastSecurities)) { return null }
      if (isIntraday || (isImpliedReturn && isOpPro)) { return null }

      const query = forecastContributorsQuery(forecastAndHorizon, { isActive, isIntraday, forecastSecurities, analyticsSegment, expand })
      return {
        id: `forecastContributors-${getQueryId(props, [...COMMON_PROPS, "forecastAndHorizon", "forecastSecurities"])}}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedForecastContributors",
    url: (props) => {
      const { forecastAndHorizon, forecastSecurities, isOpPro, isIntraday, isActive, groupingOptions, analyticsSegment, expand } = props
      const isImpliedReturn = !get(forecastAndHorizon, "forecast.id")

      if (isImpliedReturn && isEmpty(forecastSecurities)) { return null }
      if (isIntraday || (isImpliedReturn && isOpPro) || !groupingOptions) { return null }

      const query = groupedForecastContributorsQuery(forecastAndHorizon, { isActive, isIntraday, forecastSecurities, analyticsSegment, expand })
      return {
        id: `groupedForecastContributors-${getQueryId(props, [...COMMON_PROPS, "forecastAndHorizon", "forecastSecurities", "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "historicalFactorSensitivity",
    url: (props) => {
      const { decompositionType, selectedDate, isOpPro, isIntraday, intradayBatchId, isActive, analyticsSegment, expand } = props
      if (decompositionType !== "historicalFactorSensitivity" || !selectedDate) { return null }
      if (isIntraday && !intradayBatchId) { return null }
      const query = factorSensitivityQuery(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType], { isActive, isIntraday, isOpPro, analyticsSegment, expand })
      return {
        id: `historicalFactorSensitivity-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "historicalFactorSensitivityContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, analyticsSegment, expand } = props
      if (decompositionType !== "historicalFactorSensitivity" || !selectedDate) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = factorSensitivityContributorsQuery(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType], { isActive, isIntraday, isOpPro, analyticsSegment, expand })
      return {
        id: `historicalFactorSensitivityContributors-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedHistoricalFactorSensitivityContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand } = props
      if (decompositionType !== "historicalFactorSensitivity" || !selectedDate || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedFactorSensitivityContributorsQuery(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType], { isActive, isIntraday, isOpPro, analyticsSegment, expand })
      return {
        id: `groupedHistoricalFactorSensitivityContributors-${getQueryId(props, [...COMMON_PROPS, "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "predictedFactorSensitivity",
    url: (props) => {
      const { decompositionType, selectedDate, isOpPro, isIntraday, intradayBatchId, isActive, analyticsSegment, expand } = props
      if (decompositionType !== "predictedFactorSensitivity" || !selectedDate) { return null }
      if (isIntraday && !intradayBatchId) { return null }
      const query = factorSensitivityQuery(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType], { isActive, isIntraday, isOpPro, analyticsSegment, expand })
      return {
        id: `predictedFactorSensitivity-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "predictedFactorSensitivityContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, analyticsSegment, expand } = props
      if (decompositionType !== "predictedFactorSensitivity" || !selectedDate) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = factorSensitivityContributorsQuery(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType], { isActive, isIntraday, isOpPro, analyticsSegment, expand })
      return {
        id: `predictedFactorSensitivityContributors-${getQueryId(props, COMMON_PROPS)}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "groupedPredictedFactorSensitivityContributors",
    url: (props) => {
      const { decompositionType, selectedDate, isActive, isOpPro, isIntraday, intradayBatchId, groupingOptions, analyticsSegment, expand } = props
      if (decompositionType !== "predictedFactorSensitivity" || !selectedDate || !groupingOptions) { return null }
      if (isIntraday && !intradayBatchId) { return null }

      const query = groupedFactorSensitivityContributorsQuery(DECOMPOSITION_TO_FACTOR_SENSITIVITY_TYPES[decompositionType], { isActive, isIntraday, isOpPro, analyticsSegment, expand })
      return {
        id: `groupedPredictedFactorSensitivityContributors-${getQueryId(props, [...COMMON_PROPS, "groupingOptions"])}`,
        query,
        variables: getVariables(props, query),
      }
    },
  }, {
    resource: "simulatorPreferences",
    url: ({ accountId, portfolioUUID }) => {
      if (!portfolioUUID) {
        return null
      }

      return ({
        id: `simulatorPreferences-${accountId}${portfolioUUID}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "simulatorSettings",
              key: `${portfolioUUID}`,
              accountId,
            },
            {
              type: "defaultMetricUnits",
              key: accountId,
              accountId,
            },
          ],
        },
      })
    },
  }, {
    intradayChannel: ({ accountId, portfolioUUID }) => `${accountId}_${portfolioUUID}`,
    resource: "intradayLastBatch",
    as: null,
    url: ({ account, portfolioUUID, selectedDate, isIntraday }) => !isIntraday ? null : generateIntradayBatchUrl(account, portfolioUUID, selectedDate),
  }, {
    resource: "portfolioSecuritiesDescriptors",
    url: ({ account, modelId, securitiesIds, selectedDate }) => {
      if (size(securitiesIds) === 0) {
        return null
      }

      return {
        url: `/api/${account}/m/${modelId}/securities/descriptors?date=${selectedDate}`,
        id: `securitiesDescriptors${modelId}${selectedDate}${JSON.stringify(securitiesIds)}`,
        payload: { ids: securitiesIds },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    ...pick(state.modules.rebalance, ["search", "selected", "simulateActions", "searchVisible", "searchValue", "selectedCategory", "decompositionType", "error", "intradayAsOf", "setPage", "editingField", "selectedPositionWithAdv", "filtersVisible", "editing", "actionsBase"]),
    addedFilters: getAddedFilters(state, props),
    isRunning: getIsRebalanceRunning(state, props),
    factorsCategoriesHeader: getFactorsCategoriesHeader(state, props),
    trades: getTrades(state, props),
    tradeDownload: getTradeDownload(state, props),
    order: getOrder(state, props),
    numberOfTrades: getNumberOfTrades(state, props),
    selectedDataType: getSelectedDataType(state, props),
    tradesOpened: state.modules.rebalance.tradesOpened,
    baseUpdate: state.modules.rebalance.baseUpdate,
    unmodeledDates: getUnmodeledDates(state, props),
    earliestDate: getEarliestDate(state, props),
    experiment: getExperiment(state, props),
    forecasts: getForecasts(state, props),
    selectedExperiment: getSelectedExperiment(state, props),
    menuItems: getRebalanceMetrics(state, props),
    workingEquity: getWorkingEquity(state, props),
    latestDate: getLatestDate(state, props),
    positions: getPortfolioPositions(state, props),
    risk: getRiskContributors(state, props),
    combinedContributors: getRawCombinedContributors(state, props),
    columns: getColumnsByCategory(state, props),
    data: getSortedAndPaginatedDataWithEditable(state, props),
    aggregatedRow: getAggregatedData(state, props),
    widgetField: getWidgetField(state, props),
    lastRefresh: getLastRefresh(state, props),
    lastBatchId: getIntradayLastBatchId(state, props),
    hasIntradayData: getHasData(state, props),
    downloadData: getDownloadData(state, props),
    isIntradayRoute: getIsIntradayRoute(state, props),
    isIntradayRebalance: getIsIntradayRebalance(state, props),
    idSort: getIdSort(state, props),
    numberOfContributors: getNumberOfContributors(state, props),
    page: getSelectedPage(state, props),
    availableRollups: getAvailableRollups(state, props),
    currentGrouping: getGrouping(state, props),
    groupDropdown: getGroupDropdown(state, props),
    filter: getFilter(state, props),
    filterOns: getFilterOns(state, props),
    normalization: getNormalization(state, props),
    isActiveTradingDisabled: getIsActiveTradingDisabled(state, props),
    summaryRows: getSummaryRows(state, props),
    intradayRefreshable: getIntradayRefreshable(state, props),
    decompositionType: getDecompositionType(state, props),
    metricTypeOptions: getMetricTypeOptions(state, props),
    metricType: getSelectedMetricType(state, props),
    isAiTermsAccepted: getAiTermsAccepted(state, props),
  }),
  view,
  actions: {
    ...Creators,
    setSelectedDate: Creators.setRebalanceDate,
    setExpansionMode: RootCreators.setExpansionMode,
    setExperimentSelectedDate: EditExperimentCreators.setDate,
    openDialog: DialogCreators.open,
    openSecurityProfile: ProfileCreators.openSecurityProfile2,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
    addNotification: NotificationCreators.add,
  },
})
